import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Title, SubTitle, Text, Button } from "../theme";
import { checkStoredOrPromptPassword } from "../utilities/passwordCookie";

const sanitizeFilename = (value) =>
  (value || "selection")
    .toString()
    .trim()
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "selection";

const getImageExtension = (url, contentType) => {
  const typeExtension = contentType?.split("/")[1]?.split(";")[0];
  if (typeExtension) {
    return typeExtension === "jpeg" ? "jpg" : typeExtension;
  }

  const path = new URL(url).pathname;
  const extension = path.split(".").pop();
  return extension && extension !== path ? extension : "jpg";
};

const crcTable = (() => {
  const table = [];
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const getCrc32 = (bytes) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const setUint16 = (view, offset, value) => {
  view.setUint16(offset, value, true);
};

const setUint32 = (view, offset, value) => {
  view.setUint32(offset, value, true);
};

const createZipBlob = (files) => {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(({ name, bytes }) => {
    const nameBytes = encoder.encode(name);
    const crc = getCrc32(bytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    setUint32(localView, 0, 0x04034b50);
    setUint16(localView, 4, 20);
    setUint16(localView, 8, 0);
    setUint32(localView, 14, crc);
    setUint32(localView, 18, bytes.length);
    setUint32(localView, 22, bytes.length);
    setUint16(localView, 26, nameBytes.length);
    localHeader.set(nameBytes, 30);

    localParts.push(localHeader, bytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    setUint32(centralView, 0, 0x02014b50);
    setUint16(centralView, 4, 20);
    setUint16(centralView, 6, 20);
    setUint16(centralView, 10, 0);
    setUint32(centralView, 16, crc);
    setUint32(centralView, 20, bytes.length);
    setUint32(centralView, 24, bytes.length);
    setUint16(centralView, 28, nameBytes.length);
    setUint32(centralView, 42, offset);
    centralHeader.set(nameBytes, 46);

    centralParts.push(centralHeader);
    offset += localHeader.length + bytes.length;
  });

  const centralOffset = offset;
  const centralSize = centralParts.reduce((size, part) => size + part.length, 0);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  setUint32(endView, 0, 0x06054b50);
  setUint16(endView, 8, files.length);
  setUint16(endView, 10, files.length);
  setUint32(endView, 12, centralSize);
  setUint32(endView, 16, centralOffset);

  return new Blob([...localParts, ...centralParts, endRecord], {
    type: "application/zip",
  });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const EditionDownloads = () => {
  const [settings, memes, editionDocs] = useSelector((state) => [
    state.data.settings,
    state.data.memes || [],
    state.data.editions || [],
  ]);
  const [secure, setSecure] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (secure === null && settings) {
      setSecure(checkStoredOrPromptPassword(settings.password));
    }
  }, [secure, settings]);

  const editions = useMemo(() => {
    const editionNames = editionDocs.map(({ edition }) => edition);
    const fallbackNames = [
      ...new Set(memes.map((m) => m.edition).filter(Boolean)),
    ];
    const names = (editionNames.length > 0 ? editionNames : fallbackNames)
      .slice()
      .sort((a, b) => Number(a) - Number(b));

    return names.map((edition) => {
      const editionMemes = memes.filter(
        (m) => m.display && m.img && m.edition === edition
      );
      return {
        edition,
        memes: editionMemes,
      };
    });
  }, [editionDocs, memes]);

  const downloadEdition = async (edition, editionMemes, grouped) => {
    if (editionMemes.length === 0 || downloading) {
      return;
    }

    const downloadKey = `${edition}-${grouped ? "grouped" : "flat"}`;
    setDownloading(downloadKey);

    try {
      const files = await Promise.all(
        editionMemes.map(async (m, i) => {
          const response = await fetch(m.img);
          if (!response.ok) {
            throw new Error(`Could not download ${m.img}`);
          }

          const bytes = new Uint8Array(await response.arrayBuffer());
          const extension = getImageExtension(
            m.img,
            response.headers.get("content-type")
          );
          const title = sanitizeFilename(m.description || m._id);
          const filename = `${String(i + 1).padStart(3, "0")}-${title}.${extension}`;
          const firstTag = sanitizeFilename(
            m.taxonomy?.[0]?.name || "uncategorized"
          );

          return {
            name: grouped ? `${firstTag}/${filename}` : filename,
            bytes,
          };
        })
      );

      const zip = createZipBlob(files);
      const suffix = grouped ? "grouped" : "flat";
      downloadBlob(zip, `memes-${sanitizeFilename(edition)}-${suffix}.zip`);
    } catch (error) {
      console.error(error);
      window.alert("Could not create the zip download. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  if (!settings || !memes) {
    return null;
  }

  if (secure === false) {
    return (
      <Row center="xs">
        <Col xs={12}>
          <Title>MEME DOWNLOADS</Title>
          <Text>Wrong password.</Text>
        </Col>
      </Row>
    );
  }

  if (!secure) {
    return null;
  }

  return (
    <>
      <Row center="xs">
        <Col xs={12}>
          <Title>MEME DOWNLOADS</Title>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Text>
            Download memes by edition. Grouped downloads place each meme in a
            folder named after its first tag.
          </Text>
        </Col>
      </Row>

      {editions.map(({ edition, memes: editionMemes }) => (
        <Row key={edition}>
          <Col xs={12}>
            <SubTitle>
              {edition} ({editionMemes.length})
            </SubTitle>
            <Button
              noUrl
              text={`download grouped (n=${editionMemes.length})`}
              invert={editionMemes.length === 0 || Boolean(downloading)}
              action={() => downloadEdition(edition, editionMemes, true)}
            />
            <Button
              noUrl
              text={`download flat (n=${editionMemes.length})`}
              invert={editionMemes.length === 0 || Boolean(downloading)}
              action={() => downloadEdition(edition, editionMemes, false)}
            />
          </Col>
        </Row>
      ))}
    </>
  );
};

export default EditionDownloads;
